import type { ReactNode } from "react";
import clsx from "clsx";

export type InlineAlertTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface InlineAlertProps {
  tone?: InlineAlertTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  role?: "alert" | "status" | "note";
}

const toneClassName: Record<InlineAlertTone, string> = {
  neutral: "border-border-subtle bg-surface-secondary text-content-secondary",
  info: "border-border-accent/40 bg-primary/10 text-content-accent",
  success: "border-state-success/30 bg-state-successSoft text-state-success",
  warning: "border-state-warning/30 bg-state-warningSoft text-state-warning",
  danger: "border-border-danger bg-state-dangerSoft text-state-danger",
};

export const InlineAlert = ({
  tone = "neutral",
  title,
  children,
  action,
  className,
  role = tone === "danger" ? "alert" : "status",
}: InlineAlertProps) => (
  <div
    className={clsx(
      "rounded-control border px-4 py-3 text-body",
      toneClassName[tone],
      className,
    )}
    role={role}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? (
          <div className={clsx(title && "mt-1", "leading-5")}>{children}</div>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  </div>
);

export default InlineAlert;
